/**
 * Manual evidence: the agent's own notes as plain text (never markdown or HTML)
 * and its artifacts as external links. Nothing Cofactor ran backs this item.
 *
 * @module features/proofs/components/ManualEvidenceBody
 */
import React from 'react';
import { Text } from '@chatsdet/ui';
import { sha7 } from '../evidenceKinds';
import type { ProofEvidence } from '../types';

type Manual = Extract<ProofEvidence, { kind: 'manual' }>;

/** The row's one-line form: the first non-empty line of the notes. */
export const manualFirstLine = (item: Manual): string => item.notes.split('\n').find((line) => line.trim() !== '')?.trim() ?? '';

/** Artifacts are agent-supplied strings; only https ones become links. */
const isHttpsUrl = (value: string) => /^https:\/\//i.test(value);

export const ManualEvidenceBody: React.FC<{ item: Manual }> = ({ item }) => (
  <div className="space-y-3">
    <Text color="muted" size="sm">{`Noted at ${sha7(item.provedSha)}`}</Text>
    <div data-testid="manual-notes" className="whitespace-pre-wrap break-words text-sm">
      {item.notes}
    </div>
    {item.artifacts.length > 0 && (
      <ul className="space-y-1 text-sm">
        {item.artifacts.map((artifact, index) => (
          <li key={index} className="break-all">
            {isHttpsUrl(artifact) ? (
              <a href={artifact} target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
                {artifact}
              </a>
            ) : (
              artifact
            )}
          </li>
        ))}
      </ul>
    )}
  </div>
);
